import { useEffect, useMemo, useState } from 'react';
import { UsersRound } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Label } from '~/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import type { ClassOfferingFromQuery } from '../SchoolYearView/coursesTypes';
import { useCopyClassRoster } from './useCopyClassRoster';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => Promise<unknown> | unknown;
  offerings: ReadonlyArray<ClassOfferingFromQuery>;
}

interface TargetResult {
  targetClassOfferingId: string;
  targetDisplayName: string;
  addedCount: number;
  reactivatedCount: number;
  alreadyActiveCount: number;
  capacitySkippedCount: number;
  ineligibleSkippedCount: number;
  copiedCount: number;
  skippedCount: number;
  gradebookMembershipAddedCount: number;
  gradebookMembershipReactivatedCount: number;
  gradebookMembershipAlreadyActiveCount: number;
  gradebookMembershipCopiedCount: number;
  unmatchedTranscriptCourseCount: number;
  unmatchedGradebookMembershipCount: number;
  finalActiveCount: number;
  messages: ReadonlyArray<string>;
}

interface CopyResult {
  sourceStudentCount: number;
  targetCount: number;
  addedCount: number;
  reactivatedCount: number;
  alreadyActiveCount: number;
  capacitySkippedCount: number;
  ineligibleSkippedCount: number;
  copiedCount: number;
  skippedCount: number;
  gradebookMembershipAddedCount: number;
  gradebookMembershipReactivatedCount: number;
  gradebookMembershipAlreadyActiveCount: number;
  gradebookMembershipCopiedCount: number;
  unmatchedTranscriptCourseCount: number;
  unmatchedGradebookMembershipCount: number;
  targetResults: ReadonlyArray<TargetResult>;
}

function offeringLabel(offering: ClassOfferingFromQuery) {
  return `${offering.term.name} · Section ${offering.section}`;
}

export default function CopyRosterDialog({ open, onOpenChange, onComplete, offerings }: Props) {
  const sortedOfferings = useMemo(
    () => [...offerings].sort((a, b) => {
      const termComparison = (a.term.position ?? Number.MAX_SAFE_INTEGER)
        - (b.term.position ?? Number.MAX_SAFE_INTEGER);
      return termComparison || a.section.localeCompare(b.section);
    }),
    [offerings]
  );
  const [sourceId, setSourceId] = useState('');
  const [targetIds, setTargetIds] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<CopyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshWarning, setRefreshWarning] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [copyRoster, { loading }] = useCopyClassRoster();
  const busy = loading || completing;

  useEffect(() => {
    if (!open) return;
    setSourceId((current) => {
      if (current && sortedOfferings.some((offering) => offering.id === current)) return current;
      return sortedOfferings.find((offering) => (offering.activeStudentCount ?? 0) > 0)?.id
        ?? sortedOfferings[0]?.id
        ?? '';
    });
  }, [open, sortedOfferings]);

  const source = sortedOfferings.find((offering) => offering.id === sourceId);
  const targets = sortedOfferings.filter(
    (offering) => offering.id !== sourceId && offering.term.id !== source?.term.id
  );
  const selectedTargetTermIds = new Set(
    targets.filter((offering) => targetIds.has(offering.id)).map((offering) => offering.term.id)
  );

  const handleSourceChange = (nextSourceId: string) => {
    setSourceId(nextSourceId);
    setTargetIds(new Set());
    setError(null);
    setRefreshWarning(null);
  };

  const toggleTarget = (targetId: string) => {
    const target = targets.find((offering) => offering.id === targetId);
    if (!target) return;

    setTargetIds((current) => {
      const next = new Set(current);
      if (next.has(targetId)) next.delete(targetId);
      else {
        const termAlreadySelected = targets.some(
          (offering) => offering.term.id === target.term.id && current.has(offering.id)
        );
        if (termAlreadySelected) return current;
        next.add(targetId);
      }
      return next;
    });
  };

  const handleCopy = async () => {
    if (!sourceId || targetIds.size === 0) return;
    setError(null);
    setRefreshWarning(null);
    setCompleting(true);

    try {
      const response = await copyRoster({
        variables: {
          input: {
            sourceClassOfferingId: sourceId,
            targetClassOfferingIds: Array.from(targetIds),
          },
        },
      });
      const payload = response.data?.copyClassRoster;
      const userErrors = payload?.userErrors;
      if (userErrors?.length) {
        setError(userErrors.map((item) => item.message).join(', '));
        return;
      }
      if (!payload) {
        setError('The roster copy did not return a result.');
        return;
      }

      try {
        await onComplete();
      } catch {
        setRefreshWarning(
          'The roster was copied, but this page could not refresh its counts. Reopen the course if they look out of date.'
        );
      }
      setResult(payload as CopyResult);
    } catch (copyError) {
      setError(copyError instanceof Error ? copyError.message : 'Roster copy failed.');
    } finally {
      setCompleting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setTargetIds(new Set());
      setResult(null);
      setError(null);
      setRefreshWarning(null);
    }
    onOpenChange(nextOpen);
  };

  const copyAnother = () => {
    setResult(null);
    setTargetIds(new Set());
    setError(null);
    setRefreshWarning(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[620px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Copy roster across terms</DialogTitle>
          <DialogDescription>
            Copy active students to other classes in this course. Existing target students are preserved,
            and every target gradebook follows the target class roster automatically.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center gap-2 font-medium">
                <UsersRound className="h-4 w-4" />
                Copied {result.copiedCount} roster placement{result.copiedCount === 1 ? '' : 's'} to {result.targetCount} class{result.targetCount === 1 ? '' : 'es'}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {result.addedCount} added, {result.reactivatedCount} reactivated, {result.alreadyActiveCount} already active
                {result.skippedCount > 0 ? `, ${result.skippedCount} skipped` : ''}.
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {result.gradebookMembershipCopiedCount} target gradebook membership{result.gradebookMembershipCopiedCount === 1 ? '' : 's'} synchronized,{' '}
                {result.gradebookMembershipAlreadyActiveCount} already active.
              </p>
            </div>

            <div className="space-y-2">
              {result.targetResults.map((targetResult) => (
                <div key={targetResult.targetClassOfferingId} className="rounded-md border p-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">{targetResult.targetDisplayName}</p>
                    <p className="text-muted-foreground">{targetResult.finalActiveCount} active</p>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    {targetResult.addedCount} added · {targetResult.reactivatedCount} reactivated · {targetResult.alreadyActiveCount} already active
                    {targetResult.skippedCount > 0 ? ` · ${targetResult.skippedCount} skipped` : ''}
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    {targetResult.gradebookMembershipCopiedCount} target gradebook membership{targetResult.gradebookMembershipCopiedCount === 1 ? '' : 's'} synchronized ·{' '}
                    {targetResult.gradebookMembershipAlreadyActiveCount} already active
                  </p>
                  {targetResult.messages.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-700">
                      {targetResult.messages.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="roster-copy-source">Copy active students from</Label>
              <select
                id="roster-copy-source"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={sourceId}
                onChange={(event) => handleSourceChange(event.target.value)}
              >
                {sortedOfferings.map((offering) => (
                  <option key={offering.id} value={offering.id}>
                    {offeringLabel(offering)} ({offering.activeStudentCount ?? 0} active)
                  </option>
                ))}
              </select>
              {source && (
                <p className="text-xs text-muted-foreground">
                  Only the {source.activeStudentCount ?? 0} active student{source.activeStudentCount === 1 ? '' : 's'} will be copied.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Copy to</Label>
              <div className="max-h-64 overflow-y-auto rounded-md border">
                {targets.map((offering) => {
                  const selected = targetIds.has(offering.id);
                  const siblingSelected = !selected && selectedTargetTermIds.has(offering.term.id);

                  return (
                    <label
                      key={offering.id}
                      className={`flex items-center gap-3 border-b p-3 last:border-0 ${
                        siblingSelected
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer hover:bg-muted/30'
                      }`}
                    >
                      <Checkbox
                        aria-label={`Copy to ${offeringLabel(offering)}`}
                        checked={selected}
                        disabled={siblingSelected}
                        onCheckedChange={() => toggleTarget(offering.id)}
                      />
                      <span className="flex-1 text-sm">
                        <span className="font-medium">{offeringLabel(offering)}</span>
                        <span className="block text-muted-foreground">
                          {offering.activeStudentCount ?? 0}{offering.capacity ? `/${offering.capacity}` : ''} students
                        </span>
                        {siblingSelected && (
                          <span className="block text-xs text-muted-foreground">
                            Another {offering.term.name} section is already selected.
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Choose at most one target class per term. Capacity limits are honored. Dropped or transferred students in a target class are reactivated.
                Existing target gradebooks receive the complete active class roster; gradebooks are not created automatically.
              </p>
            </div>
          </div>
        )}

        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {refreshWarning && (
          <p role="status" className="text-sm text-amber-700">{refreshWarning}</p>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button variant="outline" onClick={copyAnother}>Copy another roster</Button>
              <Button onClick={() => handleOpenChange(false)}>Done</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button onClick={handleCopy} disabled={busy || !sourceId || targetIds.size === 0}>
                {busy
                  ? 'Copying…'
                  : targetIds.size === 0
                    ? 'Select target classes'
                    : `Copy roster to ${targetIds.size} class${targetIds.size === 1 ? '' : 'es'}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
